'use client';

import React from 'react';

interface BarChartProps {
  title: string;
  data: { label: string; value: number; color?: string }[];
  maxValue?: number;
  showValues?: boolean;
  unit?: string;
}

export function BarChart({
  title,
  data,
  maxValue,
  showValues = true,
  unit = ''
}: BarChartProps) {
  const max = maxValue || Math.max(...data.map((d) => d.value));

  const defaultColors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-red-500',
    'bg-cyan-500'
  ];

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <h3 className="font-bold text-gray-900 mb-6">{title}</h3>
      <div className="space-y-4">
        {data.map((item, index) => {
          const percentage = (item.value / max) * 100;
          const colorClass = item.color || defaultColors[index % defaultColors.length];

          return (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
                {showValues && (
                  <span className="text-sm font-bold text-gray-900">
                    {typeof item.value === 'number'
                      ? item.value.toLocaleString()
                      : item.value}
                    {unit}
                  </span>
                )}
              </div>
              <div className="w-full h-8 bg-gray-100 rounded-lg overflow-hidden">
                <div
                  className={`h-full ${colorClass} transition-all duration-500 flex items-center justify-end pr-2`}
                  style={{ width: `${percentage}%` }}
                >
                  {percentage > 15 && showValues && (
                    <span className="text-xs font-bold text-white">{percentage.toFixed(0)}%</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface LineChartPoint {
  x: string | number;
  y: number;
}

interface LineChartProps {
  title: string;
  data: LineChartPoint[];
  xLabel?: string;
  yLabel?: string;
  yUnit?: string;
  height?: number;
}

export function LineChart({
  title,
  data,
  xLabel,
  yLabel,
  yUnit = '',
  height = 300
}: LineChartProps) {
  if (data.length === 0) return null;

  const maxY = Math.max(...data.map((d) => d.y));
  const minY = Math.min(...data.map((d) => d.y), 0);
  const range = maxY - minY || 1; // Prevent division by zero
  const padding = range * 0.1;

  // Use consistent units for viewBox - wider aspect ratio for better fit
  const chartWidth = 1000; // Fixed width in viewBox units
  const chartHeight = 400; // Fixed height in viewBox units
  const plotHeight = chartHeight - 80; // Account for padding
  const plotWidth = chartWidth - 100; // Account for padding
  const leftPadding = 80;
  const bottomPadding = 60;

  const points = data.map((point, index) => {
    const x = leftPadding + (index / Math.max(data.length - 1, 1)) * plotWidth;
    const normalizedY = ((point.y - (minY - padding)) / (range + padding * 2));
    const y = chartHeight - bottomPadding - (normalizedY * plotHeight);
    return { x, y, label: point.x, value: point.y };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');

  // Format currency values for Y-axis
  const formatValue = (value: number) => {
    if (yUnit === '$') {
      const absValue = Math.abs(value);
      if (absValue >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (absValue >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${value.toFixed(0)}`;
    }
    return value.toLocaleString();
  };

  // Calculate Y-axis tick values
  const yTicks = [
    minY - padding,
    minY + (range + padding * 2) * 0.25,
    minY + (range + padding * 2) * 0.5,
    minY + (range + padding * 2) * 0.75,
    maxY + padding
  ];

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <h3 className="font-bold text-gray-900 mb-4">{title}</h3>
      <div className="relative w-full" style={{ height: `${height}px` }}>
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className="w-full h-full"
          preserveAspectRatio="none"
        >
          {/* Y-axis grid lines and labels */}
          {yTicks.map((tickValue, index) => {
            const normalizedY = ((tickValue - (minY - padding)) / (range + padding * 2));
            const y = chartHeight - bottomPadding - (normalizedY * plotHeight);
            return (
              <g key={index}>
                <line
                  x1={leftPadding}
                  y1={y}
                  x2={chartWidth - 20}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray={index === 2 ? "none" : "4 4"}
                />
                <text
                  x={leftPadding - 10}
                  y={y + 4}
                  textAnchor="end"
                  className="text-xs fill-gray-600"
                  style={{ fontSize: '12px' }}
                >
                  {formatValue(tickValue)}
                </text>
              </g>
            );
          })}

          {/* X-axis */}
          <line
            x1={leftPadding}
            y1={chartHeight - bottomPadding}
            x2={chartWidth - 20}
            y2={chartHeight - bottomPadding}
            stroke="#9ca3af"
            strokeWidth="2"
          />

          {/* Y-axis */}
          <line
            x1={leftPadding}
            y1={20}
            x2={leftPadding}
            y2={chartHeight - bottomPadding}
            stroke="#9ca3af"
            strokeWidth="2"
          />

          {/* Area fill under the line */}
          {points.length > 0 && (
            <path
              d={`${pathD} L ${points[points.length - 1].x},${chartHeight - bottomPadding} L ${points[0].x},${chartHeight - bottomPadding} Z`}
              fill="#3b82f6"
              fillOpacity="0.1"
            />
          )}

          {/* Line */}
          <path d={pathD} fill="none" stroke="#3b82f6" strokeWidth="3" />

          {/* Points */}
          {points.map((point, index) => (
            <g key={index}>
              <circle cx={point.x} cy={point.y} r="6" fill="white" stroke="#3b82f6" strokeWidth="3" />
              <circle cx={point.x} cy={point.y} r="3" fill="#3b82f6" />
            </g>
          ))}

          {/* X-axis labels */}
          {points.map((point, index) => (
            <text
              key={index}
              x={point.x}
              y={chartHeight - bottomPadding + 20}
              textAnchor="middle"
              className="text-xs fill-gray-600"
              style={{ fontSize: '12px' }}
            >
              {point.label}
            </text>
          ))}

          {/* Y-axis label */}
          {yLabel && (
            <text
              x={20}
              y={chartHeight / 2}
              textAnchor="middle"
              className="text-xs fill-gray-600"
              style={{ fontSize: '12px' }}
              transform={`rotate(-90, 20, ${chartHeight / 2})`}
            >
              {yLabel}
            </text>
          )}

          {/* X-axis label */}
          {xLabel && (
            <text
              x={chartWidth / 2}
              y={chartHeight - 10}
              textAnchor="middle"
              className="text-xs fill-gray-600"
              style={{ fontSize: '12px' }}
            >
              {xLabel}
            </text>
          )}
        </svg>
      </div>
    </div>
  );
}

interface PieChartProps {
  title: string;
  data: { label: string; value: number; color?: string }[];
  showLegend?: boolean;
  size?: number;
}

export function PieChart({ title, data, showLegend = true, size = 200 }: PieChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const defaultColors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#8b5cf6', // purple
    '#f59e0b', // orange
    '#ef4444', // red
    '#06b6d4' // cyan
  ];

  let currentAngle = -90; // Start at top
  const slices = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const angle = (item.value / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;

    currentAngle += angle;

    const startX = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
    const startY = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
    const endX = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
    const endY = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
    const largeArcFlag = angle > 180 ? 1 : 0;

    const path = [
      `M 50 50`,
      `L ${startX} ${startY}`,
      `A 40 40 0 ${largeArcFlag} 1 ${endX} ${endY}`,
      `Z`
    ].join(' ');

    return {
      path,
      color: item.color || defaultColors[index % defaultColors.length],
      label: item.label,
      value: item.value,
      percentage
    };
  });

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <h3 className="font-bold text-gray-900 mb-4">{title}</h3>
      <div className="flex items-center gap-8">
        <svg width={size} height={size} viewBox="0 0 100 100" className="flex-shrink-0">
          {slices.map((slice, index) => (
            <path key={index} d={slice.path} fill={slice.color} />
          ))}
        </svg>

        {showLegend && (
          <div className="flex-1 space-y-2">
            {slices.map((slice, index) => (
              <div key={index} className="flex items-center gap-3">
                <div
                  className="w-4 h-4 rounded flex-shrink-0"
                  style={{ backgroundColor: slice.color }}
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-gray-900">{slice.label}</div>
                  <div className="text-xs text-gray-600">
                    {slice.value.toLocaleString()} ({slice.percentage.toFixed(1)}%)
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
